/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: DialRuleRestrictionFormView.tsx
 🗓️ Created: 2025-01-27
 🧩 Description:
    Form view for creating and editing Dial Rule Restrictions.

 🔧 Notes:
    - Refactored from monolithic 766-line file
    - Uses hooks for state management
    - Uses DialRuleRow component for rule rows
    - Maintains drag & drop functionality
    - Maintains QuickSearchModal functionality

 🔁 Last Updated: 2025-01-27 by Rodrigo Cuadra (Refactored)
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Shield, Search, Plus, Copy } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { Input } from '../../components/ui/input';
import Toggle from '../../components/ui/Toggle';
import { Button } from '../../components/ui/button';
import { ModuleFormHeader } from '../../components/layouts';
import FixedActionBar from '../../components/ui/FixedActionBar';
import { Label } from '../../components/ui/label';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { FormBox } from '../../components/ui/FormBox';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import { useDialRuleRestrictionData } from './hooks/useDialRuleRestrictionData';
import { useDialRuleRestrictionForm } from './hooks/useDialRuleRestrictionForm';
import { DialRuleRow } from './components/DialRuleRow';
import { deleteDialRuleRestriction, fetchDialRuleRestrictionsForQuickList } from './api/dialRuleRestrictionsApi';
import { navigateToDialRuleRestrictionDuplicate } from './dialRuleRestrictionsActions';
import { DialRuleRestriction } from './types';

type FormFieldProps = {
  id: string;
  label: string;
  tooltip: string;
  children: ReactNode;
  required?: boolean;
};

const FormField = ({ id, label, tooltip, children, required }: FormFieldProps) => (
  <>
    <SimpleTooltip content={tooltip}>
      <Label htmlFor={id} className="text-left">
        {label}
        {required ? ' *' : ''}
      </Label>
    </SimpleTooltip>
    {children}
  </>
);

export default function DialRuleRestrictionFormView() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const duplicateId = searchParams.get('duplicate');
  const navigate = useNavigate();
  const { t } = useI18n();
  const { notifications, removeNotification, showSuccess, showError } = useNotifications();

  const mode = id === 'new' ? 'create' : 'edit';
  const isEditMode = mode === 'edit';

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [showQuickList, setShowQuickList] = useState(false);
  const [quickListSearch, setQuickListSearch] = useState('');
  const [quickListRestrictions, setQuickListRestrictions] = useState<DialRuleRestriction[]>([]);
  const [isLoadingQuickList, setIsLoadingQuickList] = useState(false);

  const { isLoading, formData: loadedFormData } = useDialRuleRestrictionData(id, duplicateId);

  const {
    formData,
    errors,
    isSaving,
    hasChanges,
    handleBaseChange,
    handleAddRule,
    handleRemoveRule,
    handleUpdateRule,
    handleDragEnd: onDragEnd,
    handleSubmit,
    handleCancel,
  } = useDialRuleRestrictionForm(loadedFormData, id, isEditMode);

  // Enhanced save handler for useSaveWithAction
  const handleSaveInternal = useCallback(async () => {
    const savedData = await handleSubmit();
    // If no changes were detected, return current formData to allow navigation
    if (!savedData) {
      return formData;
    }
    return savedData;
  }, [handleSubmit, formData]);

  // Use reusable hook for save actions
  const handleSaveWithAction = useSaveWithAction({
    onSave: handleSaveInternal,
    listPath: '/pbx/class-of-service/dial-restriction',
    newPath: '/pbx/class-of-service/dial-restriction/new',
    mode: isEditMode ? 'edit' : 'create',
    getEditPath: (savedData) => `/pbx/class-of-service/dial-restriction/${savedData?.uuid || savedData?.id || id}`,
    getFormData: () => formData,
    storageKey: 'dialRuleRestrictions:saveAndNewData',
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) {
        return;
      }
      onDragEnd(active.id as string, over.id as string | null);
    },
    [onDragEnd]
  );

  const loadQuickList = useCallback(async () => {
    setIsLoadingQuickList(true);
    try {
      const restrictions = await fetchDialRuleRestrictionsForQuickList(t);
      setQuickListRestrictions(restrictions);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : t('dialRuleRestrictions.messages.loadError');
      showError(message);
      setQuickListRestrictions([]);
    } finally {
      setIsLoadingQuickList(false);
    }
  }, [showError, t]);

  useEffect(() => {
    if (showQuickList) {
      loadQuickList();
    }
  }, [loadQuickList, showQuickList]);

  const handleDeleteClick = useCallback(() => {
    if (isEditMode && id) {
      setShowDeleteModal(true);
    }
  }, [id, isEditMode]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!isEditMode || !id) {
      return;
    }

    setIsDeleting(true);
    try {
      await deleteDialRuleRestriction(id, t);

      showSuccess(t('dialRuleRestrictions.messages.deleteSuccess'));
      navigate('/pbx/class-of-service/dial-restriction');
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : t('dialRuleRestrictions.messages.deleteError');
      showError(message);
      setShowDeleteModal(false);
    } finally {
      setIsDeleting(false);
    }
  }, [id, isEditMode, navigate, showError, showSuccess, t]);

  const handleDuplicateFromForm = useCallback(() => {
    if (!isEditMode || !id) return;
    navigateToDialRuleRestrictionDuplicate(navigate, id);
  }, [isEditMode, id, navigate]);

  const handleQuickListSelect = useCallback(
    (restrictionId: string | number) => {
      setShowQuickList(false);
      setQuickListSearch('');
      navigate(`/pbx/class-of-service/dial-restriction/${restrictionId}`, { replace: true });
    },
    [navigate]
  );

  const filteredQuickList = useMemo(() => {
    const query = quickListSearch.trim().toLowerCase();
    if (!query) {
      return quickListRestrictions;
    }

    return quickListRestrictions.filter(
      (restriction) =>
        restriction.name?.toLowerCase().includes(query) ||
        restriction.description?.toLowerCase().includes(query)
    );
  }, [quickListRestrictions, quickListSearch]);

  if (isLoading || !formData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="p-6"><ModuleFormHeader
        icon={<Shield className="h-4 w-4 text-primary" />}
        title={
          isEditMode
            ? `${t('dialRuleRestrictions.editRestriction')} - ${formData.name || ''}`
            : t('dialRuleRestrictions.createRestriction')
        }
        backButtonText={t('common.list')}
        onBack={() => navigate('/pbx/class-of-service/dial-restriction')}
        actions={[
          {
            icon: <Search className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('dialRuleRestrictions.quickListLabel'),
            onClick: () => setShowQuickList(true),
          },
          ...(isEditMode
            ? [
              {
                icon: <Copy className="h-5 w-5 text-muted-foreground" />,
                tooltip: t('common.duplicate'),
                onClick: handleDuplicateFromForm,
              },
            ]
            : []),
          {
            icon: <Plus className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('dialRuleRestrictions.actions.add'),
            onClick: () => navigate('/pbx/class-of-service/dial-restriction/new'),
          },
        ]}
      />

      <div className="mt-8">
        <form className="space-y-6 pb-24">
          <FormBox
            title={t('dialRuleRestrictions.sections.basicInformation')}
            icon={<Shield className="h-5 w-5" />}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
              <FormField
                id="name"
                label={t('dialRuleRestrictions.fields.name.label')}
                tooltip={t('dialRuleRestrictions.fields.name.tooltip')}
                required
              >
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(event) => handleBaseChange('name', event.target.value)}
                  placeholder={t('dialRuleRestrictions.fields.name.placeholder')}
                  className={errors.name ? 'border-red-500' : ''}
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-500">{errors.name}</p>
                )}
              </FormField>
              <FormField
                id="description"
                label={t('dialRuleRestrictions.fields.description.label')}
                tooltip={t('dialRuleRestrictions.fields.description.tooltip')}
              >
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(event) => handleBaseChange('description', event.target.value)}
                  placeholder={t('dialRuleRestrictions.fields.description.placeholder')}
                />
              </FormField>
              <FormField
                id="enabled"
                label={t('dialRuleRestrictions.enabled')}
                tooltip={t('dialRuleRestrictions.fields.enabled.tooltip')}
              >
                <Toggle
                  id="enabled"
                  value={formData.enabled}
                  onChange={(value) => handleBaseChange('enabled', value)}
                />
              </FormField>
            </div>
          </FormBox>

          <FormBox title={t('dialRuleRestrictions.outboundRules')} icon={<Shield className="h-5 w-5" />}>
            <div className="flex items-center justify-between mb-4">
              <div className="text-sm text-muted-foreground">
                {formData.rules.length} {t('dialRuleRestrictions.rules')}
              </div>
              <Button type="button" size="sm" onClick={handleAddRule}>
                <Plus className="h-4 w-4 mr-2" />
                {t('dialRuleRestrictions.actions.addRule')}
              </Button>
            </div>

            <div className="overflow-x-auto w-full">
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <table className="w-full border-collapse table-fixed">
                  <thead>
                    <tr className="border-b border-border text-sm text-foreground">
                      <th className="py-3 px-2 text-center w-[5%]">
                        {t('dialRuleRestrictions.move')}
                      </th>
                      <th className="py-3 px-4 text-center w-[12%]">
                        {t('dialRuleRestrictions.type')}
                      </th>
                      <th className="py-3 px-4 text-left w-[23%]">
                        {t('dialRuleRestrictions.pattern')}
                      </th>
                      <th className="py-3 px-4 text-center w-[10%]">
                        {t('dialRuleRestrictions.allowed')}
                      </th>
                      <th className="py-3 px-4 text-left w-[18%]">
                        {t('dialRuleRestrictions.announcement')}
                      </th>
                      <th className="py-3 px-4 text-center w-[12%]">
                        {t('dialRuleRestrictions.playMaxDuration')}
                      </th>
                      <th className="py-3 px-4 text-center w-[12%]">
                        {t('dialRuleRestrictions.maxDurationSeconds')}
                      </th>
                      <th className="py-3 px-4 text-center w-[12%]">
                        {t('dialRuleRestrictions.requirePassword')}
                      </th>
                      <th className="py-3 px-4 text-center w-[8%]">
                        {t('common.actions')}
                      </th>
                    </tr>
                  </thead>
                  <SortableContext
                    items={formData.rules.map((rule) => rule.clientId)}
                    strategy={verticalListSortingStrategy}
                  >
                    <tbody>
                      {formData.rules.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="py-8 text-center text-muted-foreground">
                            {t('dialRuleRestrictions.messages.noRules')}
                          </td>
                        </tr>
                      ) : (
                        formData.rules.map((rule, index) => (
                          <DialRuleRow
                            key={rule.clientId}
                            rule={rule}
                            index={index}
                            onUpdate={handleUpdateRule}
                            onRemove={handleRemoveRule}
                          />
                        ))
                      )}
                    </tbody>
                  </SortableContext>
                </table>
              </DndContext>
            </div>
          </FormBox>
        </form>
      </div>

      <FixedActionBar
        onSaveWithAction={handleSaveWithAction}
        onCancel={handleCancel}
        onDelete={isEditMode ? handleDeleteClick : undefined}
        isSaving={isSaving}
        isEditMode={isEditMode}
        useSaveDropdown={true}
        saveText={t('dialRuleRestrictions.actions.save')}
        cancelText={t('dialRuleRestrictions.actions.cancel')}
        deleteText={t('common.delete')}
      />

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title={t('dialRuleRestrictions.deleteRestriction')}
        message={t('dialRuleRestrictions.messages.confirmDeleteMessage', {
          name: formData.name || '',
        })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={isDeleting}
      />

      <QuickSearchModal
        isOpen={showQuickList}
        onClose={() => {
          setShowQuickList(false);
          setQuickListSearch('');
        }}
        title={t('dialRuleRestrictions.quickList.title')}
        items={filteredQuickList}
        isLoadingInitial={isLoadingQuickList && quickListRestrictions.length === 0}
        isSearching={false}
        searchValue={quickListSearch}
        onSearchChange={setQuickListSearch}
        onItemSelect={(restriction) => handleQuickListSelect(restriction.uuid || restriction.id)}
        renderItem={(restriction) => (
          <button
            type="button"
            onClick={() => handleQuickListSelect(restriction.uuid || restriction.id)}
            className="w-full p-3 text-left border border-border rounded-lg hover:bg-muted/30 transition-colors"
          >
            <div className="font-medium text-foreground truncate">{restriction.name}</div>
            {restriction.description && (
              <div className="text-sm text-muted-foreground truncate">
                {restriction.description}
              </div>
            )}
            <div className="text-xs text-muted-foreground mt-1">
              {restriction._count?.rules ?? 0} {t('dialRuleRestrictions.rules')}
            </div>
          </button>
        )}
        searchPlaceholder={t('dialRuleRestrictions.quickList.searchPlaceholder')}
        emptyMessage={t('dialRuleRestrictions.quickList.noResults')}
        headerIcon={<Shield className="h-5 w-5" />}
        minSearchChars={0}
      />
    </div>
  );
}
